import React from 'react';
import { Button, Dropdown, Modal, Input, Tooltip, message as antMessage } from 'antd';
import CancelRecurringRefillModal from 'components/CancelRecurringRefillModal';
import { MinusOutlined } from '@ant-design/icons';
import dayjs from 'utils/dayjs';
import SuccessToast from 'components/SuccessToast';
import AiTextEditor from 'components/AI/AiTextEditor';
import { EAIEditorType } from 'types/ai';
import { sendMessage } from 'api/inbox';
import { snoozeTextVisit, declineTextVisit } from 'api/textVisit';
import { getUserId } from 'utils/localstore';
import { getTimeLeft } from 'pages/dashboard/TextVisitPage/utils';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import s from './s.module.less';

const { TextArea } = Input;

function ChevronDownIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
        >
            <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#374151"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function CloseXIcon({
    className,
    size = 24,
    onClick,
}: {
    className?: string;
    size?: number;
    onClick?: React.MouseEventHandler<SVGSVGElement>;
}) {
    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
            onClick={onClick}
        >
            <path
                d="M6 18L18 6M6 6L18 18"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function CalendarIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
        >
            <path
                d="M6.66667 5.83333V2.5M13.3333 5.83333V2.5M5.83334 9.16667H14.1667M4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V5.83333C17.5 4.91286 16.7538 4.16667 15.8333 4.16667H4.16667C3.24619 4.16667 2.5 4.91286 2.5 5.83333V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5Z"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export type RefillTaskItem = {
    patientName: string;
    description: string;
    submittedAt?: string;
    submittedOn: string;
    lastUpdatedAt?: string;
    lastUpdatedOn?: string;
    isRecurring?: boolean;
    // Some API payloads use recurrenceId / recurrentId to indicate recurring tasks.
    // We keep both to avoid brittle UI gating.
    recurrenceId?: number;
    recurrentId?: number;
    hasOutstandingBalance?: boolean;
    clientId?: number;
    textVisitId?: number;
    textVisitUuid?: string;
    providerTreatmentSubmitted?: boolean;
    assignedProviderAt?: string;
    canModifyRecurrence?: boolean;
    startAt?: string;
    specialty?: string;
};

type Props = {
    totalLabel: string;
    onClose: () => void;
    items: RefillTaskItem[];
    onSnooze?: (item: RefillTaskItem) => void;
    onDecline?: (item: RefillTaskItem, reason: string) => void;
    onCancel?: (item: RefillTaskItem, cancelAll: boolean) => void;
    onEdit?: (item: RefillTaskItem) => void;
    onSendMessage?: (item: RefillTaskItem) => void;
    onView?: (item: RefillTaskItem) => void;
    onUpdated?: () => void;
};

type SortKey = 'oldest' | 'newest';

const getDeclineMessage = (firstName: string) =>
    `Hi ${firstName}, I'm unable to fulfill this prescription refill at this time.\nPlease book a visit so we can review your request and discuss next steps.`;

const RefillTasksPopoverContent = (props: Props) => {
    const { totalLabel, onClose, items, onSnooze, onDecline, onCancel, onEdit, onSendMessage, onView, onUpdated } =
        props;
    const [sortKey, setSortKey] = React.useState<SortKey>('oldest');
    const navigate = useNavigate();

    const [declineVisible, setDeclineVisible] = React.useState(false);
    const [declineItem, setDeclineItem] = React.useState<RefillTaskItem | null>(null);
    const [declineReason, setDeclineReason] = React.useState('');
    const [decliningLoading, setDecliningLoading] = React.useState(false);

    const [cancelVisible, setCancelVisible] = React.useState(false);
    const [cancelItem, setCancelItem] = React.useState<RefillTaskItem | null>(null);

    const [toastVisible, setToastVisible] = React.useState(false);
    const [toastMessage, setToastMessage] = React.useState('');
    const toastTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

    const [composeVisible, setComposeVisible] = React.useState(false);
    const [composeMinimized, setComposeMinimized] = React.useState(false);
    const [composeItem, setComposeItem] = React.useState<RefillTaskItem | null>(null);
    const [composeContent, setComposeContent] = React.useState('');
    const [composeSending, setComposeSending] = React.useState(false);
    const [composeResetFlag, setComposeResetFlag] = React.useState<{}>({});

    const showToast = React.useCallback((msg: string) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToastMessage(msg);
        setToastVisible(true);
        toastTimerRef.current = setTimeout(() => setToastVisible(false), 5000);
    }, []);

    React.useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    const sortedItems = React.useMemo(() => {
        const year = dayjs().year();
        const toDateValue = (v: string | undefined) => {
            if (!v) return 0;
            const d = dayjs(`${v} ${year}`, 'MMM D YYYY');
            return d.isValid() ? d.valueOf() : 0;
        };
        const toIsoValue = (v: string | undefined) => {
            if (!v) return 0;
            const d = dayjs(v);
            return d.isValid() ? d.valueOf() : 0;
        };
        const clone = [...items];
        clone.sort((a, b) => {
            // Sort strictly by "Submitted on" timestamp (not display text).
            const aPrimary = toIsoValue(a.submittedAt) || toDateValue(a.submittedOn);
            const bPrimary = toIsoValue(b.submittedAt) || toDateValue(b.submittedOn);
            if (aPrimary !== bPrimary) {
                return sortKey === 'oldest' ? aPrimary - bPrimary : bPrimary - aPrimary;
            }
            const aSecondary = toIsoValue(a.lastUpdatedAt) || toDateValue(a.lastUpdatedOn);
            const bSecondary = toIsoValue(b.lastUpdatedAt) || toDateValue(b.lastUpdatedOn);
            return sortKey === 'oldest' ? aSecondary - bSecondary : bSecondary - aSecondary;
        });
        return clone;
    }, [items, sortKey]);

    const sortMenuItems = [
        {
            key: 'oldest',
            label: 'Sort by oldest',
            className: sortKey === 'oldest' ? s.menuItemActive : undefined,
            onClick: () => setSortKey('oldest'),
        },
        {
            key: 'newest',
            label: 'Sort by newest',
            className: sortKey === 'newest' ? s.menuItemActive : undefined,
            onClick: () => setSortKey('newest'),
        },
    ];

    const handleSnooze = async (item: RefillTaskItem) => {
        try {
            if (item.textVisitId) {
                const snoozeUntil = dayjs().utc().add(1, 'day').startOf('day').toISOString();
                const res = await snoozeTextVisit({ textVisitId: item.textVisitId, snoozeUntil });
                if (res?.error) throw new Error('snooze failed');
            }
            showToast('Prescription refill task snoozed until tomorrow.');
            onSnooze?.(item);
            onUpdated?.();
            onClose();
        } catch {
            antMessage.error('Failed to snooze. Please try again.');
        }
    };

    const handleOpenDecline = (item: RefillTaskItem) => {
        const firstName = item.patientName.split(' ')[0] || item.patientName;
        setDeclineItem(item);
        setDeclineReason(getDeclineMessage(firstName));
        setDeclineVisible(true);
        onClose();
    };

    const handleDeclineConfirm = async () => {
        if (!declineItem) return;
        const providerId = getUserId();

        setDecliningLoading(true);
        try {
            if (declineItem.textVisitId) {
                const res = await declineTextVisit({
                    textVisitId: declineItem.textVisitId,
                    declineReason,
                });
                if (res?.error) throw new Error('decline failed');
            }

            if (declineItem.clientId && providerId) {
                await sendMessage({
                    providerId,
                    clientId: declineItem.clientId,
                    message: declineReason.replace(/<[^>]+>/g, ''),
                });
            }

            onDecline?.(declineItem, declineReason);
            showToast('Prescription refill request declined. Message sent to the patient.');
            setDeclineVisible(false);
            setDeclineItem(null);
            onUpdated?.();
            onClose();
        } catch {
            antMessage.error('Failed to decline. Please try again.');
        } finally {
            setDecliningLoading(false);
        }
    };

    const handleOpenCancel = (item: RefillTaskItem) => {
        setCancelItem(item);
        setCancelVisible(true);
        onClose();
    };

    const handleOpenCompose = (item: RefillTaskItem) => {
        setComposeItem(item);
        setComposeContent('');
        setComposeResetFlag({});
        setComposeMinimized(false);
        setComposeVisible(true);
    };

    const handleComposeDiscard = () => {
        setComposeContent('');
        setComposeResetFlag({});
    };

    const handleComposeSend = async () => {
        const providerId = getUserId();
        if (!composeItem?.clientId || !composeContent || !providerId) {
            antMessage.error('Please enter a message.');
            return;
        }
        setComposeSending(true);
        const res = await sendMessage({
            providerId,
            clientId: composeItem.clientId,
            message: composeContent.replace(/<[^>]+>/g, ''),
        });
        setComposeSending(false);
        if (!res.error) {
            showToast('Message sent successfully.');
            setComposeVisible(false);
            setComposeItem(null);
            setComposeContent('');
            onUpdated?.();
            onClose();
        }
    };

    const handleComposeClose = () => {
        setComposeVisible(false);
        setComposeItem(null);
        setComposeContent('');
        setComposeResetFlag({});
    };

    const handleGoToClient = (item: RefillTaskItem) => {
        if (!item.clientId) return;
        navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${item.clientId}`);
        onClose();
    };

    const isRecurringTask = (item: RefillTaskItem) =>
        Boolean(item.recurrentId ?? item.recurrenceId ?? item.isRecurring);

    const isUpcoming = (item: RefillTaskItem) => {
        if (!item.startAt) return false;
        return dayjs.utc(item.startAt).isAfter(dayjs.utc());
    };

    const getMoreMenuItems = (item: RefillTaskItem) => [
        {
            key: 'snooze',
            label: 'Snooze until tomorrow',
            onClick: () => handleSnooze(item),
        },
        ...(isRecurringTask(item) && item.canModifyRecurrence
            ? [
                  {
                      key: 'edit',
                      label: 'Edit',
                      onClick: () => {
                          onEdit?.(item);
                          onClose();
                      },
                  },
              ]
            : []),
        ...(!isRecurringTask(item) && item.textVisitId && isUpcoming(item)
            ? [
                  {
                      key: 'edit',
                      label: 'Edit',
                      onClick: () => {
                          onEdit?.(item);
                          onClose();
                      },
                  },
              ]
            : []),
        ...(item.textVisitId
            ? [
                  {
                      key: 'decline',
                      label: isRecurringTask(item) ? 'Cancel' : 'Decline',
                      onClick: () => {
                          if (isRecurringTask(item)) {
                              handleOpenCancel(item);
                          } else {
                              handleOpenDecline(item);
                          }
                      },
                  },
              ]
            : []),
        {
            key: 'send_message',
            label: 'Send message',
            onClick: () => {
                handleOpenCompose(item);
                onSendMessage?.(item);
            },
        },
    ];

    return (
        <>
            <div className={s.wrap} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                <div className={s.header}>
                    <div className={s.title}>{totalLabel}</div>
                    <Dropdown
                        menu={{ items: sortMenuItems }}
                        trigger={['click']}
                        placement="bottomLeft"
                        overlayClassName={s.menuOverlay}
                    >
                        <Button className={s.sortBtn} size="small">
                            {sortKey === 'oldest' ? 'Sort by oldest' : 'Sort by newest'}
                            <ChevronDownIcon className={s.sortBtnIcon} />
                        </Button>
                    </Dropdown>
                    <button type="button" className={s.closeBtn} aria-label="Close" onClick={onClose}>
                        <CloseXIcon />
                    </button>
                </div>

                <div className={s.list}>
                    {sortedItems.map((item, idx) => (
                        <div
                            key={`${item.patientName}-${item.description}`}
                            className={`${s.item} ${idx > 0 ? s.itemBorder : ''}`}
                        >
                            <div className={s.itemLeft}>
                                <div className={s.itemTitle}>
                                    <span className={s.itemTitleText}>
                                        <span className={s.patientName}>
                                            {isRecurringTask(item) && <span className={s.recurringIcon}>🔁</span>}
                                            {item.clientId ? (
                                                <button
                                                    type="button"
                                                    className={s.patientNameLink}
                                                    onClick={() => handleGoToClient(item)}
                                                >
                                                    {item.patientName}
                                                </button>
                                            ) : (
                                                item.patientName
                                            )}
                                        </span>
                                        <span className={s.itemDesc}>{` - ${item.description}`}</span>
                                    </span>
                                    {item.hasOutstandingBalance && (
                                        <Tooltip
                                            title="The patient has an outstanding balance from one or more prior refill visits."
                                            overlayClassName={s.balanceTooltip}
                                        >
                                            <span className={s.balanceIconWrap}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M10.0012 7.5V9.16667M10.0012 12.5H10.0096M4.22775 15.8333H15.7748C17.0578 15.8333 17.8596 14.4444 17.2181 13.3333L11.4446 3.33333C10.8031 2.22222 9.19937 2.22222 8.55787 3.33333L2.78437 13.3333C2.14287 14.4444 2.94475 15.8333 4.22775 15.8333Z"
                                                        stroke="#C45407"
                                                        strokeWidth="1.66667"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                <div className={s.meta}>
                                    <CalendarIcon className={s.metaIcon} />
                                    <span className={s.metaText}>
                                        {[
                                            item.startAt
                                                ? `${isRecurringTask(item) ? 'Next refill' : 'Refill'} on ${dayjs
                                                      .utc(item.startAt)
                                                      .format('MMM D')}`
                                                : null,
                                            `Submitted on ${item.submittedOn}`,
                                            // Only one-time tasks show "Last updated on"; recurring tasks omit it.
                                            !isRecurringTask(item) && item.lastUpdatedOn
                                                ? `Last updated on ${item.lastUpdatedOn}`
                                                : null,
                                        ]
                                            .filter(Boolean)
                                            .join(' | ')}
                                    </span>
                                </div>
                            </div>
                            <div className={s.itemRight}>
                                {item.assignedProviderAt &&
                                    (() => {
                                        const timeInfo = getTimeLeft(item.assignedProviderAt);
                                        return (
                                            <div className={s.timeBadge} style={{ background: timeInfo.color }}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 18 18"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M9 3.75V9L12 10.5M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z"
                                                        stroke="#1F2937"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                <span>{timeInfo.text}</span>
                                            </div>
                                        );
                                    })()}
                                <div className={s.itemActions}>
                                    <Button className={s.viewBtn} size="small" onClick={() => onView?.(item)}>
                                        View
                                    </Button>
                                    <Dropdown
                                        menu={{ items: getMoreMenuItems(item) }}
                                        trigger={['click']}
                                        placement="bottomRight"
                                        overlayClassName={s.menuOverlay}
                                    >
                                        <button type="button" className={s.moreBtn} aria-label="More">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M10 6C8.89543 6 8 5.10457 8 4C8 2.89543 8.89543 2 10 2C11.1046 2 12 2.89543 12 4C12 5.10457 11.1046 6 10 6Z"
                                                    fill="#111827"
                                                />
                                                <path
                                                    d="M10 12C8.89543 12 8 11.1046 8 10C8 8.89543 8.89543 8 10 8C11.1046 8 12 8.89543 12 10C12 11.1046 11.1046 12 10 12Z"
                                                    fill="#111827"
                                                />
                                                <path
                                                    d="M10 18C8.89543 18 8 17.1046 8 16C8 14.8954 8.89543 14 10 14C11.1046 14 12 14.8954 12 16C12 17.1046 11.1046 18 10 18Z"
                                                    fill="#111827"
                                                />
                                            </svg>
                                        </button>
                                    </Dropdown>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Decline Modal */}
            <Modal
                open={declineVisible}
                onCancel={() => setDeclineVisible(false)}
                footer={null}
                closable
                width={543}
                className={s.declineModal}
                destroyOnClose
                zIndex={1050}
            >
                <div className={s.modalHeader}>
                    <h3 className={s.modalTitle}>Decline this prescription refill request</h3>
                </div>
                <div className={s.modalBody}>
                    <p className={s.modalDesc}>
                        You won&apos;t be able to prescribe this medication for this patient.
                        <br />
                        Please enter the reason and notify the patient.
                    </p>
                    <TextArea
                        className={s.modalTextarea}
                        rows={5}
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder="Enter the reason..."
                    />
                </div>
                <div className={s.modalFooter}>
                    <Button disabled={decliningLoading} onClick={() => setDeclineVisible(false)}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        className={s.primaryBtn}
                        loading={decliningLoading}
                        onClick={handleDeclineConfirm}
                    >
                        Confirm and send message
                    </Button>
                </div>
            </Modal>

            {/* Cancel Recurring Modal */}
            <CancelRecurringRefillModal
                open={cancelVisible}
                textVisitId={cancelItem?.textVisitId}
                patientName={cancelItem?.patientName}
                showSuccessToast={showToast}
                onClose={() => {
                    setCancelVisible(false);
                    setCancelItem(null);
                }}
                onSuccess={(cancelAll) => {
                    if (cancelItem) onCancel?.(cancelItem, cancelAll);
                    onUpdated?.();
                    onClose();
                }}
            />

            {/* New message compose window */}
            {composeVisible && (
                <div className={`${s.newMsgWrap} ${composeMinimized ? '' : s.newMsgWrapShow}`}>
                    <div className={s.nTitle}>
                        <span>New message</span>
                        <div className={s.nTitleActions}>
                            <MinusOutlined
                                className={s.nTitleIcon}
                                onClick={() => setComposeMinimized(!composeMinimized)}
                            />
                            <CloseXIcon size={16} className={s.nTitleIcon} onClick={handleComposeClose} />
                        </div>
                    </div>
                    {!composeMinimized && (
                        <>
                            <div className={s.nContent}>
                                <div className={s.nLabel}>Client</div>
                                <div className={s.nClientName}>{composeItem?.patientName}</div>
                                <div className={s.nLabel}>Message</div>
                                <div className={s.inputWrap}>
                                    <AiTextEditor
                                        size="small"
                                        rows={6}
                                        maxLength={3000}
                                        onChange={(e) => {
                                            if (typeof e === 'string') {
                                                setComposeContent(e);
                                            } else {
                                                setComposeContent(e ? e.target.value : '');
                                            }
                                        }}
                                        value={composeContent}
                                        type={EAIEditorType.PROSPECT}
                                        resetFlag={composeResetFlag}
                                        placeholder=""
                                    />
                                </div>
                            </div>
                            <div className={s.nFooter}>
                                <Button style={{ marginRight: '16px' }} onClick={handleComposeDiscard}>
                                    Discard
                                </Button>
                                <Button
                                    loading={composeSending}
                                    disabled={!composeContent}
                                    type="primary"
                                    onClick={handleComposeSend}
                                >
                                    Send
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}

            <SuccessToast visible={toastVisible} message={toastMessage} onClose={() => setToastVisible(false)} />
        </>
    );
};

export default RefillTasksPopoverContent;
